// The conversation thread, styled to the mocks: user turns as right-aligned
// accent bubbles, assistant turns as the "Thinking…" status (while live) above
// the prose answer. Auto-scrolls to the newest content as events stream in.

import React, { useEffect, useRef } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import styles from "../sqAi.module.css";
import ChartCard from "../../chart-agent/components/ChartCard";
import ThinkingStatus from "./ThinkingStatus";

export default function Thread({ messages, onSuggestionClick, suggestionsDisabled }) {
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages]);

  return (
    <div className={styles.thread}>
      {messages.map((msg, i) => (
        <div className={styles.row} key={msg.id}>
          {msg.role === "user" ? (
            <UserBubble message={msg} />
          ) : (
            <AssistantTurn
              message={msg}
              isLast={i === messages.length - 1}
              onSuggestionClick={onSuggestionClick}
              suggestionsDisabled={suggestionsDisabled}
            />
          )}
        </div>
      ))}
      <div ref={bottomRef} />
    </div>
  );
}

function UserBubble({ message }) {
  return (
    <div className={styles.userRow}>
      <div className={styles.userBubble}>{message.text}</div>
    </div>
  );
}

function AssistantTurn({ message, isLast, onSuggestionClick, suggestionsDisabled }) {
  const caption = (message.caption || "").trim();
  const hasChart = message.charts.length > 0;
  const finishedEmpty = message.done && !message.error && !hasChart && !caption;
  const thinking = !message.done;
  const suggestions = message.suggestions || [];
  // #2a/#2b: result caption — "Last 3 months · by reported date · Hazards · category: Fall from Height".
  const meta = message.resultMeta || null;
  const vf = meta && meta.valueFilter;
  const valueSeg = vf && Array.isArray(vf.matched) && vf.matched.length
    ? `${vf.concept || "value"}: ${vf.matched.join(", ")}`
    : null;
  const metaCaption = meta
    ? [meta.window, meta.dateBasisLabel ? `by ${meta.dateBasisLabel}` : null, meta.modules && meta.modules.length ? meta.modules.join(" / ") : null, valueSeg]
        .filter(Boolean)
        .join(" · ")
    : "";

  return (
    <div>
      <ThinkingStatus steps={message.thinkingSteps || []} thinking={thinking} thinkingTick={message.thinkingTick || 0} />

      {message.assumptions.map((a, i) => (
        <div className={styles.assumption} key={i}>
          <span aria-hidden="true">ⓘ</span>
          <span>
            {a.text}
            {a.rationale && <span className={styles.assumptionRationale}> — {a.rationale}</span>}
          </span>
        </div>
      ))}

      {message.charts.map((spec, i) => (
        <ChartCard key={i} spec={spec} />
      ))}

      {caption && (
        <div className={`${styles.prose} ${styles.assistantCaption}`}>
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{caption}</ReactMarkdown>
        </div>
      )}

      {message.error && (
        <div className={styles.errorBox}>
          <span aria-hidden="true">⚠</span>
          <div>
            <div>{message.error.message || "Something went wrong."}</div>
            {message.error.code && <div className={styles.assumptionRationale}>{message.error.code}</div>}
          </div>
        </div>
      )}

      {finishedEmpty && (
        <p className={styles.prose}>I couldn't put together a result for that one. Could you ask it a different way?</p>
      )}

      {message.done && !message.error && !finishedEmpty && metaCaption && (
        <div className={styles.resultMeta}>{metaCaption}</div>
      )}

      {isLast && suggestions.length > 0 && (
        <div className={styles.suggestRow}>
          {suggestions.map((s, i) => (
            <button
              key={s.query || s.label || i}
              type="button"
              className={styles.example}
              onClick={() => onSuggestionClick && onSuggestionClick(s.query || s.label)}
              disabled={suggestionsDisabled}
            >
              {s.label}
            </button>
          ))}
        </div>
      )}

      <SqlAccordion sqls={message.sqls} />
    </div>
  );
}

// Dev-only: the SQL only reaches the UI in uiMode "dev" / when devSql is set.
function SqlAccordion({ sqls }) {
  if (!sqls || sqls.length === 0) return null;
  return (
    <details className={styles.sqlDetails}>
      <summary className={styles.sqlToggle}>Show generated SQL{sqls.length > 1 ? ` (${sqls.length})` : ""}</summary>
      <div>
        {sqls.map((s, i) => (
          <div key={s.id || i}>
            <div className={styles.sqlName}>{s.name || `query ${i + 1}`}</div>
            <pre className={styles.sqlPre}>{s.sql}</pre>
          </div>
        ))}
      </div>
    </details>
  );
}
